import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, BadgeCheck, AlertTriangle, Loader2, Undo2 } from "lucide-react";
import adminApi from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

const pad = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const mondayOf = (dateStr) => {
  const d = new Date(`${dateStr}T12:00:00`);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const short = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

// v2.8+E345 · approve a Mon–Sun pay week; edits after approval flip it back.
export const PayrollApprovalBar = ({ rangeStart, onWeekChange, refreshKey }) => {
  const mon = mondayOf(rangeStart);
  const week = ymd(mon);
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/admin/timesheets/approval", { params: { week_start: week } });
      setSt(data);
    } catch (_e) { setSt(null); }
  }, [week]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const go = (n) => { const m = addDays(mon, 7 * n); onWeekChange({ start: ymd(m), end: ymd(addDays(m, 6)) }); };

  const call = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); setSt(data); toast.success(msg); } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update approval");
    } finally { setBusy(false); }
  };
  const approve = () => call(() => adminApi.post("/admin/timesheets/approval", { week_start: week }), "Week approved");
  const undo = () => call(() => adminApi.delete(`/admin/timesheets/approval/${week}`), "Approval removed");

  const status = st?.status || "unapproved";
  const tone = status === "approved" ? "border-emerald-500/40 bg-emerald-500/5"
    : status === "changed" ? "border-rose-500/40 bg-rose-500/5" : "border-zinc-200 dark:border-zinc-800";

  return (
    <Card className={`p-4 flex flex-wrap items-center justify-between gap-3 ${tone}`} data-testid="payroll-approval-bar">
      <div className="flex items-center gap-2">
        <Button size="icon" variant="ghost" onClick={() => go(-1)} data-testid="payroll-week-prev" className="h-8 w-8"><ChevronLeft className="h-4 w-4"/></Button>
        <div>
          <div className="text-[11px] uppercase tracking-widest font-bold text-zinc-500">Pay week</div>
          <div className="text-sm font-bold" data-testid="payroll-week-label">{short(mon)} – {short(addDays(mon, 6))}</div>
        </div>
        <Button size="icon" variant="ghost" onClick={() => go(1)} data-testid="payroll-week-next" className="h-8 w-8"><ChevronRight className="h-4 w-4"/></Button>
      </div>
      <div className="text-sm flex-1 min-w-[200px]" data-testid="payroll-approval-status">
        {status === "approved" && (
          <span className="flex items-center gap-1.5 text-emerald-700 dark:text-emerald-300 font-semibold">
            <BadgeCheck className="h-4 w-4"/>Approved by {st.approved_by} · {when(st.approved_at)} · ${st.approved_totals?.pay?.toFixed(2)}
          </span>
        )}
        {status === "changed" && (
          <span className="flex items-start gap-1.5 text-rose-700 dark:text-rose-300 font-semibold">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0"/>Changed since approval — {st.change_reason} ({st.changed_by}, {when(st.changed_at)}). Re-approve needed.
          </span>
        )}
        {status === "unapproved" && <span className="text-zinc-500">Not approved yet{st ? ` · ${st.live_totals.hours.toFixed(2)} hrs · $${st.live_totals.pay.toFixed(2)}` : ""}</span>}
      </div>
      <div className="flex items-center gap-2">
        {status !== "approved" && (
          <Button size="sm" onClick={approve} disabled={busy || !st} data-testid="payroll-approve-btn" className="h-9 text-xs bg-emerald-500 hover:bg-emerald-600 text-white font-bold">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <BadgeCheck className="h-3.5 w-3.5 mr-1"/>}
            {status === "changed" ? "Re-approve week" : "Approve this week"}
          </Button>
        )}
        {status !== "unapproved" && (
          <Button size="sm" variant="ghost" onClick={undo} disabled={busy} data-testid="payroll-unapprove-btn" className="h-9 text-xs text-zinc-500">
            <Undo2 className="h-3.5 w-3.5 mr-1"/>Undo
          </Button>
        )}
      </div>
    </Card>
  );
};

export default PayrollApprovalBar;
